/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRecoilValue } from "recoil";
import { IUser } from "@interfaces/Interfaces";
import { userAtom } from "@state/State";
import * as DatabaseService from "@controllers/DatabaseService";
import { useLoggedIn } from "@hooks/auth/useLoggedIn";

type RefreshSupervisorEmailsFromRole = {
  refreshSupervisorEmailsFromRole: (user: IUser, programID: string, roleID: string) => Promise<void>;
  /** Loads supervisor emails immediately (e.g. on button press). No-op if user/program/role/token missing. */
  fetchSupervisorEmailsFromRole: () => Promise<any | undefined>;
};

const requestSupervisorEmailsFromRole = async (user: IUser, programID: string, roleID: string): Promise<any> => {
  if (!user || !user.userData) {
    throw new Error("User is required");
  }

  if (!user.token) {
    throw new Error("User token is required");
  }

  if (!programID || !roleID) {
    throw new Error("programID and roleID are required");
  }

  const supervisorEmails = await DatabaseService.getSupervisorEmailsFromRole({
    contactID: user.userData.contactID,
    token: user.token,
    programID,
    roleID,
  });

  return supervisorEmails;
};

export const supervisorEmailsFromRoleQueryKey = (
  contactID: number | string | undefined,
  programID: string | undefined,
  roleID: string | undefined
) => {
  return ["supervisor-emails-from-role", contactID, programID, roleID] as const;
};

export const useSupervisorEmailsFromRole = (
  programID: string = "",
  roleID: string = "",
  shouldLoad?: boolean
): RefreshSupervisorEmailsFromRole & {
  supervisorEmails: any | undefined;
  isPending: boolean;
  isFetching: boolean;
  error: Error | null;
} => {
  const user = useRecoilValue(userAtom);
  const loggedIn = useLoggedIn();
  const queryClient = useQueryClient();

  const isEnabled = Boolean(loggedIn && user && user.userData && user.token && programID && roleID && shouldLoad);

  const {
    data: supervisorEmails,
    isPending,
    isFetching,
    error,
  } = useQuery({
    queryKey: supervisorEmailsFromRoleQueryKey(user?.userData?.contactID, programID, roleID),
    queryFn: () => requestSupervisorEmailsFromRole(user!, programID, roleID),
    enabled: isEnabled,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  useEffect(() => {
    if (!user || !user.userData) {
      queryClient.removeQueries({ queryKey: ["supervisor-emails-from-role"] });
    }
  }, [user, queryClient]);

  const refreshSupervisorEmailsFromRole = useCallback(
    async (_user: IUser, _programID: string, _roleID: string): Promise<void> => {
      try {
        if (!_user || !_user.userData || !_programID || !_roleID) {
          return;
        }

        queryClient.invalidateQueries({
          queryKey: supervisorEmailsFromRoleQueryKey(_user.userData.contactID, _programID, _roleID),
        });
      } catch (err) {
        console.log(err);
      }
    },
    [queryClient]
  );

  const fetchSupervisorEmailsFromRole = useCallback(async (): Promise<any | undefined> => {
    if (!user?.userData || !user.token || !programID || !roleID) {
      return undefined;
    }

    try {
      return await queryClient.fetchQuery({
        queryKey: supervisorEmailsFromRoleQueryKey(user.userData.contactID, programID, roleID),
        queryFn: () => requestSupervisorEmailsFromRole(user, programID, roleID),
        staleTime: 1000 * 60 * 60 * 24, // 24 hours
        gcTime: 1000 * 60 * 60 * 24, // 24 hours
        retry: false,
      });
    } catch (err) {
      console.log(err);
      return undefined;
    }
  }, [user, programID, roleID, queryClient]);

  return {
    supervisorEmails,
    isPending,
    isFetching,
    error,
    refreshSupervisorEmailsFromRole,
    fetchSupervisorEmailsFromRole,
  };
};
